import type { GameData, ActionCompletionToast, LocationId } from '../game/types';

interface ToastElement {
  element: HTMLElement;
  toast: ActionCompletionToast;
  dismissTimer: number;
}

export class ToastManager {
  private container: HTMLElement;
  private gameData: GameData;
  private toasts: ToastElement[] = [];
  private readonly DISMISS_TIME = 5000; // 5 seconds
  private readonly MAX_TOASTS = 5;

  constructor(container: HTMLElement, gameData: GameData) {
    this.container = container;
    this.gameData = gameData;
    this.setup();
  }

  private setup(): void {
    this.container.className = 'toast-container';
  }

  show(toast: ActionCompletionToast): void {
    // Remove oldest toast if at max
    if (this.toasts.length >= this.MAX_TOASTS) {
      const oldest = this.toasts.shift();
      if (oldest) {
        this.dismissToast(oldest);
      }
    }

    const element = this.createToastElement(toast);
    this.container.appendChild(element);

    const toastElement: ToastElement = {
      element,
      toast,
      dismissTimer: window.setTimeout(() => {
        this.dismissToast(toastElement);
      }, this.DISMISS_TIME)
    };

    this.toasts.push(toastElement);

    // Trigger slide-in animation
    requestAnimationFrame(() => {
      element.classList.add('toast-visible');
    });
  }

  private createToastElement(toast: ActionCompletionToast): HTMLElement {
    const strings = this.gameData.uiStrings;
    const locationName = strings.locations?.[toast.location] || toast.location;
    
    const element = document.createElement('div');
    element.className = 'toast';
    element.setAttribute('data-action-type', toast.actionType);
    
    element.innerHTML = `
      <div class="toast-header">
        <div class="toast-researcher">${toast.researcherName}</div>
        <div class="toast-location">${locationName}</div>
      </div>
      <div class="toast-summary">${toast.summary}</div>
    `;

    // Add click to dismiss
    element.addEventListener('click', () => {
      const toastElement = this.toasts.find(t => t.element === element);
      if (toastElement) {
        this.dismissToast(toastElement);
      }
    });

    return element;
  }

  private dismissToast(toastElement: ToastElement): void {
    if (toastElement.dismissTimer) {
      clearTimeout(toastElement.dismissTimer);
    }

    const index = this.toasts.indexOf(toastElement);
    if (index > -1) {
      this.toasts.splice(index, 1);
    }

    toastElement.element.classList.add('toast-dismissing');
    
    setTimeout(() => {
      if (toastElement.element.parentNode) {
        toastElement.element.parentNode.removeChild(toastElement.element);
      }
    }, 300); // Match CSS transition time
  }

  update(): void {
    // Update toast positions - toasts stack from top
    this.toasts.forEach((toastElement, index) => {
      toastElement.element.style.top = `${index * 90}px`; // 80px height + 10px gap
    });
  }

  clearAll(): void {
    this.toasts.forEach(toast => {
      if (toast.dismissTimer) {
        clearTimeout(toast.dismissTimer);
      }
    });
    this.toasts = [];
    this.container.innerHTML = '';
  }
}

